
import { Component, Vue } from "vue-property-decorator";
import { KnowledgeBaseArticle, KnowledgeBaseCategory } from "@/survey";

function emptyCategory(): KnowledgeBaseCategory {
  return {
    id: null,
    tableName: "kb_category",
    name: "",
    description: "",
    color: "#3157b7",
    sortOrder: 0,
    active: true
  };
}

@Component({})
export default class KnowledgeBaseAdmin extends Vue {
  categories: KnowledgeBaseCategory[] = [];
  articles: KnowledgeBaseArticle[] = [];
  categoryForm: KnowledgeBaseCategory = emptyCategory();
  articleQuery = "";
  articleStatus: string | null = null;
  selectedArticleIds: number[] = [];
  selectedCategoryIds: number[] = [];
  categoryMoveToId: number | null = null;

  categoryFields = [
    { key: "selected", label: "", sortable: false },
    { key: "name", sortable: true },
    { key: "description", sortable: true },
    { key: "color", sortable: false },
    { key: "sortOrder", sortable: true },
    { key: "active", sortable: true },
    { key: "actions", sortable: false }
  ];

  articleFields = [
    { key: "selected", label: "", sortable: false },
    { key: "title", sortable: true },
    { key: "categoryName", label: "Category", sortable: true },
    { key: "status", sortable: true },
    { key: "authorName", label: "Author", sortable: true },
    { key: "updatedAt", label: "Updated", sortable: true },
    { key: "views", sortable: true },
    { key: "actions", sortable: false }
  ];

  statusFilterOptions = [
    { value: null, text: "All Statuses" },
    { value: "published", text: "Published" },
    { value: "draft", text: "Draft" },
    { value: "private", text: "Private" }
  ];

  async created() {
    await Promise.all([this.loadCategories(), this.loadArticles()]);
  }

  get categoryMoveOptions(): { value: number | null; text: string }[] {
    return [
      { value: null, text: "Select Destination" },
      ...this.categories
        .filter(category => category.id !== null && this.selectedCategoryIds.indexOf(category.id as number) < 0)
        .map(category => ({ value: category.id as number, text: category.name }))
    ];
  }

  async loadCategories() {
    this.categories = await Vue.prototype.$surveyClient.getKnowledgeBaseCategories(false);
    this.selectedCategoryIds = this.selectedCategoryIds.filter(id => this.categories.some(category => category.id === id));
    if (this.categoryMoveToId !== null && !this.categories.some(category => category.id === this.categoryMoveToId)) {
      this.categoryMoveToId = null;
    }
  }

  async loadArticles() {
    const params: any = {};
    if (this.articleQuery.trim()) {
      params.query = this.articleQuery.trim();
    }
    if (this.articleStatus !== null) {
      params.status = this.articleStatus;
    }
    this.articles = await Vue.prototype.$surveyClient.getKnowledgeBaseAdminArticles(params);
    this.selectedArticleIds = this.selectedArticleIds.filter(id => this.articles.some(article => article.id === id));
  }

  toggleArticle(id: number, checked: boolean) {
    this.selectedArticleIds = checked
      ? Array.from(new Set(this.selectedArticleIds.concat([id])))
      : this.selectedArticleIds.filter(selectedId => selectedId !== id);
  }

  toggleCategory(id: number, checked: boolean) {
    this.selectedCategoryIds = checked
      ? Array.from(new Set(this.selectedCategoryIds.concat([id])))
      : this.selectedCategoryIds.filter(selectedId => selectedId !== id);
    if (this.categoryMoveToId !== null && this.selectedCategoryIds.indexOf(this.categoryMoveToId) >= 0) {
      this.categoryMoveToId = null;
    }
  }

  editCategory(category: KnowledgeBaseCategory) {
    this.categoryForm = { ...category };
  }

  resetCategoryForm() {
    this.categoryForm = emptyCategory();
  }

  async saveCategory() {
    await Vue.prototype.$surveyClient.createOrUpdateKnowledgeBaseCategory(this.categoryForm);
    this.resetCategoryForm();
    await this.loadCategories();
  }

  async deleteCategory(category: KnowledgeBaseCategory) {
    if (!category.id) {
      return;
    }
    this.selectedCategoryIds = [category.id];
    if (this.categoryMoveToId === null || this.categoryMoveToId === category.id) {
      this.categoryMoveToId = null;
      alert("Select a destination category before deleting this category.");
      return;
    }
    await this.bulkDeleteCategories();
  }

  async deleteArticle(article: KnowledgeBaseArticle) {
    if (!article.id || !confirm("Delete this knowledge base article?")) {
      return;
    }
    await Vue.prototype.$surveyClient.deleteKnowledgeBaseArticle(article.id);
    await this.loadArticles();
  }

  async bulkDeleteArticles() {
    if (this.selectedArticleIds.length === 0 || !confirm("Delete the selected knowledge base articles?")) {
      return;
    }
    await Vue.prototype.$surveyClient.bulkDeleteKnowledgeBaseArticles(this.selectedArticleIds);
    this.selectedArticleIds = [];
    await this.loadArticles();
  }

  async bulkDeleteCategories() {
    if (this.selectedCategoryIds.length === 0 || this.categoryMoveToId === null) {
      alert("Select categories to delete and a destination category for their articles.");
      return;
    }
    if (!confirm("Delete the selected categories and move their articles to the selected destination?")) {
      return;
    }
    await Vue.prototype.$surveyClient.bulkDeleteKnowledgeBaseCategories(this.selectedCategoryIds, this.categoryMoveToId);
    this.selectedCategoryIds = [];
    this.categoryMoveToId = null;
    await Promise.all([this.loadCategories(), this.loadArticles()]);
  }

  statusVariant(status: string) {
    if (status === "published") {
      return "success";
    }
    if (status === "private") {
      return "dark";
    }
    return "warning";
  }

  formatDate(value: number | null | undefined) {
    return value ? new Date(value).toLocaleDateString() : "";
  }
}
